
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Quote } from "lucide-react";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";

const ProfileCarousel = () => {
  const { elementRef: titleRef, isVisible: titleVisible } = useIntersectionObserver();
  const { elementRef: cardsRef, isVisible: cardsVisible } = useIntersectionObserver();
  const { elementRef: footerRef, isVisible: footerVisible } = useIntersectionObserver();

  const profiles = [
    {
      name: "Justine",
      location: "Lille",
      image: "/lovable-uploads/justine-profile.png",
      quote: "Je me sens accompagnée tout en gardant ma liberté d'organisation."
    },
    {
      name: "Stéphanie",
      location: "Montpellier", 
      image: "/lovable-uploads/ce0e76d4-a63b-4844-ac1e-3d987cb0e804.png",
      quote: "Un équilibre pro/perso que je n'avais jamais eu auparavant."
    },
    {
      name: "Mathilde",
      location: "Toulon",
      image: "/lovable-uploads/ce4b1145-2de3-4613-830a-3d7ab999e715.png",
      quote: "Des outils clairs et un vrai réseau pour réussir rapidement."
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-accent/30 to-secondary/10">
      <div className="container mx-auto px-6">
        <div 
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-800 ${
            titleVisible ? 'animate-slide-in-up' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">Qui sont les indépendants</span> Work&You ?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Nos indépendants viennent de tous horizons, mais partagent un même élan : 
            celui d'entreprendre <strong>en s'appuyant sur un collectif</strong>.
          </p>
        </div>

        <div 
          ref={cardsRef}
          className="grid md:grid-cols-3 gap-8 mb-12"
        >
          {profiles.map((profile, index) => (
            <Card 
              key={index} 
              className={`group hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-0 bg-white/80 backdrop-blur-sm ${
                cardsVisible 
                  ? `animate-slide-in-up animate-stagger-${index + 1}` 
                  : 'opacity-0 translate-y-10'
              }`}
            >
              <CardContent className="p-8">
                <div className="relative mb-6">
                  <div className="w-24 h-24 mx-auto rounded-full overflow-hidden ring-4 ring-primary/20 group-hover:ring-primary/40 transition-all duration-300">
                    <img 
                      src={profile.image} 
                      alt={profile.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                      width="96"
                      height="96"
                    />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <Quote className="w-4 h-4 text-white" />
                  </div>
                </div>
                
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-primary mb-2">{profile.name}</h3>
                  <div className="flex items-center justify-center text-muted-foreground mb-4">
                    <MapPin className="w-4 h-4 mr-2" />
                    <span>{profile.location}</span>
                  </div>
                  <p className="text-gray-600 italic leading-relaxed">"{profile.quote}"</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div 
          ref={footerRef}
          className={`text-center bg-white/50 backdrop-blur-sm rounded-2xl p-8 border border-primary/20 transition-all duration-800 ${
            footerVisible ? 'animate-scale-in' : 'opacity-0 scale-95'
          }`}
        >
          <p className="text-lg text-gray-700 leading-relaxed">
            Ils évoluent dans toute la France, en <strong>statut indépendant</strong> (auto-entrepreneur, SASU, profession libérale…), 
            tout en étant <strong>intégrés à la vie du réseau</strong> : réunions commerciales, formations, entraide quotidienne via Google Chat, etc.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProfileCarousel;
